import {
  LIMITED_ACCESS_KIND,
  SITE_INSIDE,
  SITE_PLACE,
  SITE_STOP,
  SITE_VEHICLES,
  type LimitedAccessKind,
  type SiteInside,
  type SitePlace,
  type SiteStop,
} from "@convex/constants/siteAccess.lib";
import type { SiteAccess } from "@convex/schemas/validators/siteAccess.lib";
import type { FreightVehicle } from "@convex/schemas/validators/freightShipment.lib";
import { stopAccessFacts } from "@shared/addresses/site-access";

const SITE_VEHICLE_LABEL: Record<(typeof SITE_VEHICLES)[number], string> = {
  cargoVan: "Cargo van",
  boxTruck: "26' box truck",
  dryVan: "53' trailer",
  flatbed: "Flatbed",
};

export const SITE_VEHICLE_OPTIONS = SITE_VEHICLES.map((value) => ({ value, label: SITE_VEHICLE_LABEL[value] }));

export const PLACE_OPTIONS: ReadonlyArray<{ value: Exclude<SitePlace, "residence">; label: string }> = [
  { value: SITE_PLACE.BUSINESS, label: "Business" },
  { value: SITE_PLACE.CONSTRUCTION_SITE, label: "Construction site" },
  { value: SITE_PLACE.TRADE_SHOW, label: "Trade show or convention" },
  { value: SITE_PLACE.LIMITED_ACCESS, label: "Limited access site" },
];

export const LIMITED_ACCESS_OPTIONS: ReadonlyArray<{ value: LimitedAccessKind; label: string }> = [
  { value: LIMITED_ACCESS_KIND.SCHOOL, label: "School or university" },
  { value: LIMITED_ACCESS_KIND.MILITARY, label: "Military base" },
  { value: LIMITED_ACCESS_KIND.PLACE_OF_WORSHIP, label: "Church or place of worship" },
  { value: LIMITED_ACCESS_KIND.FARM, label: "Farm or ranch" },
  { value: LIMITED_ACCESS_KIND.SELF_STORAGE, label: "Self-storage" },
  { value: LIMITED_ACCESS_KIND.CORRECTIONAL, label: "Prison or jail" },
  { value: LIMITED_ACCESS_KIND.GOVERNMENT, label: "Government building" },
  { value: LIMITED_ACCESS_KIND.OTHER, label: "Other gated or secured site" },
];

export const INSIDE_OPTIONS: ReadonlyArray<{ value: SiteInside; label: string }> = [
  { value: SITE_INSIDE.NONE, label: "No, at the truck" },
  { value: SITE_INSIDE.GROUND_FLOOR, label: "Yes, ground floor" },
  { value: SITE_INSIDE.UPSTAIRS, label: "Yes, upstairs or mezzanine" },
  { value: SITE_INSIDE.BACK_OF_HOUSE, label: "Yes, back of the building" },
  { value: SITE_INSIDE.LIVING_SPACE, label: "Yes, living space or garage" },
];

export function moreOptionsCount(access: SiteAccess, stop: SiteStop): number {
  const set = [
    access.place !== undefined && access.place !== SITE_PLACE.BUSINESS && access.place !== SITE_PLACE.RESIDENCE,
    access.inside !== undefined && access.inside !== SITE_INSIDE.NONE,
    access.palletJack === true,
    access.driverAssist === true,
    access.dropTrailer === true,
    stop === SITE_STOP.DELIVERY && access.lumper === true,
    stop === SITE_STOP.DELIVERY && (access.vehicles?.length ?? 0) > 0,
  ];
  return set.filter(Boolean).length;
}

const INSIDE_SHORT: Record<SiteInside, string | null> = {
  [SITE_INSIDE.NONE]: null,
  [SITE_INSIDE.GROUND_FLOOR]: "Inside, ground floor",
  [SITE_INSIDE.UPSTAIRS]: "Inside, upstairs",
  [SITE_INSIDE.BACK_OF_HOUSE]: "Inside, back of building",
  [SITE_INSIDE.LIVING_SPACE]: "Inside, living space",
};

const PLACE_SHORT: Record<SitePlace, string | null> = {
  [SITE_PLACE.BUSINESS]: null,
  [SITE_PLACE.RESIDENCE]: "Home address",
  [SITE_PLACE.CONSTRUCTION_SITE]: "Construction site",
  [SITE_PLACE.TRADE_SHOW]: "Trade show",
  [SITE_PLACE.LIMITED_ACCESS]: "Limited access",
};

export interface SiteAccessChip {
  label: string;
  caution: boolean;
}

export interface SiteAccessSummary {
  dock: string;
  forklift: string;
  appointment: string;
  chips: SiteAccessChip[];
}

export const NOT_ASKED = "Not asked yet";

function answer(value: boolean | undefined): string {
  return value === true ? "Yes" : value === false ? "No" : NOT_ASKED;
}

export function siteAccessSummary(access: SiteAccess | undefined): SiteAccessSummary {
  const facts = access ?? {};
  const chips: Array<SiteAccessChip | null> = [
    stopAccessFacts(facts).liftgate ? { label: "Liftgate needed", caution: true } : null,
    placeChip(facts),
    facts.vehicles?.length ? { label: `Only ${facts.vehicles.map(vehicleLabel).join(", ")}`, caution: true } : null,
    facts.lumper ? { label: "Lumper required", caution: true } : null,
    facts.inside ? chip(INSIDE_SHORT[facts.inside]) : null,
    facts.palletJack ? chip("Pallet jack") : null,
    facts.driverAssist ? chip("Driver assist") : null,
    facts.dropTrailer ? chip("Drop trailer") : null,
  ];
  return { dock: answer(facts.dock), forklift: answer(facts.forklift), appointment: answer(facts.appointment), chips: chips.filter((item): item is SiteAccessChip => item !== null) };
}

export function siteAccessLine(access: SiteAccess | undefined): string {
  if (!access || Object.keys(access).length === 0) return "";
  const summary = siteAccessSummary(access);
  const appointment = access.appointment ? ["Appointment required"] : [];
  return [`Dock: ${summary.dock}`, `Forklift: ${summary.forklift}`, ...appointment, ...summary.chips.map((item) => item.label)].join(" · ");
}

export function siteAccessBrief(access: SiteAccess): string {
  const equipment = [access.dock ? "Loading dock" : null, access.forklift ? "Forklift" : null];
  const notes = siteAccessSummary(access).chips.map((item) => item.label);
  return [...equipment, ...notes].filter(Boolean).join(" · ");
}

function chip(label: string | null): SiteAccessChip | null {
  return label ? { label, caution: false } : null;
}

function placeChip(access: SiteAccess): SiteAccessChip | null {
  if (!access.place) return null;
  const kind = access.place === SITE_PLACE.LIMITED_ACCESS ? LIMITED_ACCESS_OPTIONS.find((option) => option.value === access.limitedAccessKind) : undefined;
  const label = kind ? `Limited access: ${kind.label}` : PLACE_SHORT[access.place];
  return label ? { label, caution: true } : null;
}

function vehicleLabel(vehicle: FreightVehicle): string {
  return SITE_VEHICLE_OPTIONS.find((option) => option.value === vehicle)?.label ?? vehicle;
}
